import * as React from "react"
import { Loader2, Send, ShoppingCart } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDate, formatUserDateTime } from "@/lib/date-time"
import { formatMoneyAmount } from "@/lib/money"
import {
  cachedRead,
  fetchPurchases,
  runPurchases,
  updateConnection,
  type HrisConnection,
  type HrisPurchases,
} from "@/features/integrations/hris/hrisApi"

type Props = {
  connection: HrisConnection
  /** Bumped by the parent when the legal state changes, to reload availability. */
  refreshKey: number
}

const NONE = "__none__"

/**
 * Company-paid trips as committed purchases from the supplier "Travel Code" in
 * the HR system's procurement module (Cleemy Procurement for Lucca), so the
 * spend sits on the right cost center before our invoice arrives. Off by
 * default; a cancelled booking closes its purchase.
 */
export function HrisPurchasesCard({ connection, refreshKey }: Props) {
  const t = useBookingT()
  const [state, setState] = React.useState<HrisPurchases | null>(() => cachedRead<HrisPurchases>(`purchases:${connection.id}`) ?? null)
  const [categoryId, setCategoryId] = React.useState<string | null>(() => (state?.supported ? (state.categoryId ?? state.suggestedId) : null))
  const [costCenterId, setCostCenterId] = React.useState<string | null>(() => (state?.supported ? state.costCenterId : null))
  const [confirm, setConfirm] = React.useState<"on" | "off" | null>(null)
  const [busy, setBusy] = React.useState(false)

  const load = React.useCallback(() => {
    fetchPurchases(connection.id)
      .then((data) => {
        setState(data)
        if (data.supported) {
          setCategoryId(data.categoryId ?? data.suggestedId)
          setCostCenterId(data.costCenterId)
        }
      })
      .catch(() => setState(null))
  }, [connection.id])

  React.useEffect(load, [load, refreshKey])

  if (!state || !state.supported) return null

  const provider = connection.providerName
  const fill = (text: string) => text.split("{provider}").join(provider)
  const dirty = categoryId !== state.categoryId || costCenterId !== state.costCenterId
  const statusBadge = {
    sent: <Badge variant="green">{t("hris_pur_sent", "Committed")}</Badge>,
    closed: <Badge variant="frozen">{t("hris_pur_closed", "Closed")}</Badge>,
    failed: <Badge variant="destructive">{t("hris_exp_failed", "Not sent")}</Badge>,
    skipped: <Badge variant="warning">{t("hris_pur_skipped", "Not sent yet")}</Badge>,
  }

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true)
    try {
      await fn()
      toast.success(done)
      setConfirm(null)
      load()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start justify-between gap-3 max-md:flex-col">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <ShoppingCart className="size-5 text-brand-500" />
              <h2 className="text-lg font-semibold text-neutral-900">{fill(t("hris_pur_title", "Company-paid trips as purchases in {provider}"))}</h2>
              {state.enabled ? (
                <Badge variant="green">{t("hris_offers_on", "On")}</Badge>
              ) : (
                <Badge variant="frozen">{t("hris_offers_off", "Off")}</Badge>
              )}
            </div>
            <p className="max-w-2xl text-sm text-neutral-800">
              {fill(
                t(
                  "hris_pur_intro",
                  "Every trip your company pays for appears in {provider} purchasing as a committed purchase from Travel Code, on the right cost center, before our invoice arrives. A cancelled booking closes its purchase. Personal trips are never sent.",
                ),
              )}
            </p>
            {state.enabled && state.since && (
              <p className="text-sm text-neutral-800">
                {t("hris_pur_since", "Sending trips booked since {date}").replace("{date}", formatUserDateTime(state.since))}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {state.enabled && (
              <Button variant="outline" disabled={busy} onClick={() => void act(() => runPurchases(connection.id), t("hris_exp_run_queued", "Sending. The list updates in a minute."))}>
                <Send className="size-4" />
                {t("hris_exp_run", "Send now")}
              </Button>
            )}
            {state.enabled ? (
              <Button variant="outline" className="text-error-700" onClick={() => setConfirm("off")}>
                {t("hris_offers_disable", "Switch off")}
              </Button>
            ) : (
              <Button disabled={!state.available} onClick={() => setConfirm("on")}>
                {t("hris_offers_enable", "Switch on")}
              </Button>
            )}
          </div>
        </div>

        {!state.available && (
          <p className="text-sm text-neutral-800">
            {t("hris_offers_needs_legal", "Available once Schedule E and the permission to receive employee data are in force.")}
          </p>
        )}
        {state.categoriesError && <p className="text-sm text-error-700">{state.categoriesError}</p>}

        <div className="grid grid-cols-1 gap-4 border-t border-neutral-200 pt-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-900">{fill(t("hris_pur_category", "{provider} purchase category for trips"))}</Label>
            <Select value={categoryId ?? NONE} onValueChange={(v) => setCategoryId(v === NONE ? null : v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("hris_choose", "Choose")}</SelectItem>
                {state.categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-900">{t("hris_pur_cost_center", "Default cost center")}</Label>
            <Select value={costCenterId ?? NONE} onValueChange={(v) => setCostCenterId(v === NONE ? null : v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("hris_choose", "Choose")}</SelectItem>
                {state.costCenters.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-800">
              {fill(t("hris_pur_cost_center_hint", "Used when the trip's cost center has no {provider} cost center with the same code or name."))}
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            disabled={busy || !dirty}
            onClick={() =>
              void act(
                () => updateConnection(connection.id, { settings: { purchaseCategoryId: categoryId, purchaseCostCenterId: costCenterId } }),
                t("hris_offers_saved", "Saved."),
              )
            }
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            {t("hris_save", "Save")}
          </Button>
        </div>

        {state.recent.length > 0 && (
          <div className="space-y-2 border-t border-neutral-200 pt-4">
            <h3 className="text-sm font-semibold text-neutral-900">{t("hris_pur_recent", "Latest trips")}</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("hris_exp_col_date", "Date")}</TableHead>
                  <TableHead>{t("hris_pur_col_booking", "Booking")}</TableHead>
                  <TableHead className="text-right">{t("hris_exp_col_amount", "Amount")}</TableHead>
                  <TableHead>{t("hris_status", "Status")}</TableHead>
                  <TableHead>{t("hris_exp_col_note", "Details")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.recent.map((row) => (
                  <TableRow key={row.orderId}>
                    <TableCell className="whitespace-nowrap text-sm">{row.date ? formatUserDate(row.date) : "—"}</TableCell>
                    <TableCell className="text-sm">{row.orderCode ?? "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                      {row.amount !== null && row.currency ? formatMoneyAmount(row.amount, row.currency) : "—"}
                    </TableCell>
                    <TableCell>{statusBadge[row.status]}</TableCell>
                    <TableCell className="text-sm text-neutral-800">{row.note ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="text-left">
            <DialogTitle>
              {confirm === "on" ? fill(t("hris_pur_on_title", "Send company-paid trips to {provider}?")) : t("hris_pur_off_title", "Stop sending trips?")}
            </DialogTitle>
            <DialogDescription className="text-neutral-800">
              {confirm === "on"
                ? fill(
                    t(
                      "hris_pur_on_text",
                      "For each company-paid booking, Travel Code creates in {provider} a purchase from Travel Code with the traveller, their department and establishment, the category and cost center, the booking number and the amount. What is written is then governed by your agreement with {provider}. You can switch this off at any time.",
                    ),
                  )
                : fill(t("hris_pur_off_text", "Nothing more is sent. Purchases already in {provider} stay there."))}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              {t("hris_cancel", "Cancel")}
            </Button>
            <Button
              variant={confirm === "off" ? "destructive" : "default"}
              disabled={busy}
              onClick={() =>
                void act(
                  () => updateConnection(connection.id, { settings: { purchaseWriteBack: confirm === "on" } }),
                  confirm === "on" ? t("hris_exp_on_done", "Switched on.") : t("hris_exp_off_done", "Switched off."),
                )
              }
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              {confirm === "on" ? t("hris_offers_enable", "Switch on") : t("hris_offers_disable", "Switch off")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
